/**
 * CFA Institute exam windows by level (public calendar).
 * Always confirm on the official dates & fees page before registering.
 * Source references: CFA Institute schedule cycle 2026–2027.
 */

export const CFA_DATES_FEES_URL =
  'https://www.cfainstitute.org/programs/cfa-program/dates-fees';

/** @typedef {{ id: string, level: 'l1'|'l2'|'l3', label: string, start: string, end: string, date: string }} ExamWindow */

/** @type {ExamWindow[]} */
export const EXAM_WINDOWS = [
  // Level 1 — four windows / year (ids kept stable for saved profiles)
  {
    id: '2026-feb',
    level: 'l1',
    label: 'February 2026',
    start: '2026-02-02',
    end: '2026-02-08',
    date: '2026-02-02',
  },
  {
    id: '2026-may',
    level: 'l1',
    label: 'May 2026',
    start: '2026-05-12',
    end: '2026-05-18',
    date: '2026-05-12',
  },
  {
    id: '2026-aug',
    level: 'l1',
    label: 'August 2026',
    start: '2026-08-18',
    end: '2026-08-24',
    date: '2026-08-18',
  },
  {
    id: '2026-nov',
    level: 'l1',
    label: 'November 2026',
    start: '2026-11-11',
    end: '2026-11-17',
    date: '2026-11-11',
  },
  {
    id: 'l1-2027-feb',
    level: 'l1',
    label: 'February 2027',
    start: '2027-02-22',
    end: '2027-02-28',
    date: '2027-02-22',
  },
  {
    id: 'l1-2027-may',
    level: 'l1',
    label: 'May 2027',
    start: '2027-05-11',
    end: '2027-05-17',
    date: '2027-05-11',
  },
  {
    id: 'l1-2027-aug',
    level: 'l1',
    label: 'August 2027',
    start: '2027-08-15',
    end: '2027-08-22',
    date: '2027-08-15',
  },
  {
    id: 'l1-2027-nov',
    level: 'l1',
    label: 'November 2027',
    start: '2027-11-13',
    end: '2027-11-19',
    date: '2027-11-13',
  },

  // Level 2 — May / August / November (no February)
  {
    id: 'l2-2026-may',
    level: 'l2',
    label: 'May 2026',
    start: '2026-05-19',
    end: '2026-05-23',
    date: '2026-05-19',
  },
  {
    id: 'l2-2026-aug',
    level: 'l2',
    label: 'August 2026',
    start: '2026-08-25',
    end: '2026-08-29',
    date: '2026-08-25',
  },
  {
    id: 'l2-2026-nov',
    level: 'l2',
    label: 'November 2026',
    start: '2026-11-18',
    end: '2026-11-22',
    date: '2026-11-18',
  },
  {
    id: 'l2-2027-may',
    level: 'l2',
    label: 'May 2027',
    start: '2027-05-18',
    end: '2027-05-22',
    date: '2027-05-18',
  },
  {
    id: 'l2-2027-aug',
    level: 'l2',
    label: 'August 2027',
    start: '2027-08-23',
    end: '2027-08-27',
    date: '2027-08-23',
  },
  {
    id: 'l2-2027-nov',
    level: 'l2',
    label: 'November 2027',
    start: '2027-11-20',
    end: '2027-11-24',
    date: '2027-11-20',
  },

  // Level 3 — February / August only
  {
    id: 'l3-2026-feb',
    level: 'l3',
    label: 'February 2026',
    start: '2026-01-29',
    end: '2026-02-01',
    date: '2026-01-29',
  },
  {
    id: 'l3-2026-aug',
    level: 'l3',
    label: 'August 2026',
    start: '2026-08-13',
    end: '2026-08-17',
    date: '2026-08-13',
  },
  {
    id: 'l3-2027-feb',
    level: 'l3',
    label: 'February 2027',
    start: '2027-02-18',
    end: '2027-02-21',
    date: '2027-02-18',
  },
  {
    id: 'l3-2027-aug',
    level: 'l3',
    label: 'August 2027',
    start: '2027-08-11',
    end: '2027-08-14',
    date: '2027-08-11',
  },
];

const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

function parseYmd(ymd) {
  const [y, m, d] = String(ymd || '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
}

/** e.g. "11–17 November 2026" */
export function formatExamWindowRange(window) {
  if (!window?.start || !window?.end) return '';
  const a = parseYmd(window.start);
  const b = parseYmd(window.end);
  if (!a || !b) return window.label || '';
  if (a.y === b.y && a.m === b.m) {
    return `${a.d}–${b.d} ${MONTHS[a.m - 1]} ${a.y}`;
  }
  if (a.y === b.y) {
    return `${a.d} ${MONTHS[a.m - 1]} – ${b.d} ${MONTHS[b.m - 1]} ${a.y}`;
  }
  return `${a.d} ${MONTHS[a.m - 1]} ${a.y} – ${b.d} ${MONTHS[b.m - 1]} ${b.y}`;
}

export function normalizeExamLevel(level) {
  const lv = String(level || 'l1').toLowerCase();
  if (lv === 'l2' || lv === '2' || lv === 'level 2') return 'l2';
  if (lv === 'l3' || lv === '3' || lv === 'level 3') return 'l3';
  return 'l1';
}

export function getExamWindows(level = null) {
  if (!level) return EXAM_WINDOWS.slice();
  const lv = normalizeExamLevel(level);
  return EXAM_WINDOWS.filter((w) => w.level === lv);
}

export function todayYmd(now = new Date()) {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function getUpcomingExamWindows(level = 'l1', now = new Date(), { includePastSavedId = null } = {}) {
  const today = todayYmd(now);
  return getExamWindows(level).filter((w) => {
    if (includePastSavedId && w.id === includePastSavedId) return true;
    return w.end >= today;
  });
}

export function getNextExamWindow(level = 'l1', now = new Date()) {
  const upcoming = getUpcomingExamWindows(level, now);
  if (upcoming.length) return upcoming[0];
  const all = getExamWindows(level);
  return all[all.length - 1] || null;
}

export function findExamWindowByIdOrDate(idOrDate) {
  if (!idOrDate) return null;
  return (
    EXAM_WINDOWS.find((p) => p.id === idOrDate) ||
    EXAM_WINDOWS.find((p) => p.date === idOrDate) ||
    EXAM_WINDOWS.find((p) => idOrDate >= p.start && idOrDate <= p.end) ||
    null
  );
}

export function examDatesBlogPath(level = 'l1') {
  const lv = normalizeExamLevel(level);
  if (lv === 'l2') return '/blog/cfa-level-2-exam-dates-2026';
  if (lv === 'l3') return '/blog/cfa-level-3-exam-dates-2026';
  return '/blog/cfa-level-1-exam-dates-2026';
}

/** Short public line for hubs / home strips. */
export function nextExamWindowPublicLine(level = 'l1', now = new Date()) {
  const next = getNextExamWindow(level, now);
  if (!next) return '';
  const range = formatExamWindowRange(next);
  const lv = normalizeExamLevel(level);
  const label = lv === 'l2' ? 'Level 2' : lv === 'l3' ? 'Level 3' : 'Level 1';
  const isLast2026 = next.end.startsWith('2026') && !getUpcomingExamWindows(level, now).some((w) => w.id !== next.id && w.end.startsWith('2026'));
  if (lv === 'l1' && next.id === '2026-nov') {
    return `${range} is the last 2026 ${label} window`;
  }
  if (isLast2026 && next.end.startsWith('2026')) {
    return `${range} is the next ${label} window (last 2026 sitting)`;
  }
  return `Next ${label} window: ${range}`;
}

export const LEVEL_EXAM_CADENCE = {
  l1: 'Four windows per year (February, May, August, November).',
  l2: 'Three windows per year (May, August, November) — no February sitting.',
  l3: 'Two windows per year (February and August) — no May or November sitting.',
};
